import { Absence, RegularCancellationRequest, RegularSlot } from '@/types/Slots'
import { DaycareType } from '@/types/Daycare'
import { Role } from '@/types/Role'
import moment, { Moment } from 'moment-timezone'
import { CalendarEventType, EventCalendar } from '@/types/Calendar'

interface GroupedRegularSlot {
    key: string
    slots: RegularSlot[]
}

interface GroupedRegularCancellation {
    key: string
    slots: RegularCancellationRequest[]
}

export function dataRegularSlots(
    regularSlots: RegularSlot[],
    datesClosingDays: Moment[],
    daycareType: DaycareType,
    role?: Role
): EventCalendar[] {
    if ((daycareType === DaycareType.rpe || daycareType === DaycareType.laep) && role !== Role.parent) {
        return formatRPERegularSlots(regularSlots, datesClosingDays, role)
    } else {
        return formatRegularSlots(regularSlots, datesClosingDays, role)
    }
}

function formatRPERegularSlots(regularSlots: RegularSlot[], datesClosingDays: Moment[], role?: Role): EventCalendar[] {
    const groupedSlotsMap: { [key: string]: GroupedRegularSlot } = {}

    regularSlots.forEach((slot: RegularSlot) => {
        const start: string = moment(slot.start, 'HH:mm:ss').format('HH:mm')
        const end: string = moment(slot.end, 'HH:mm:ss').format('HH:mm')
        const date: string = moment(slot.date, 'DD-MM-YYYY').format('DD-MM-YYYY')
        const day: number = slot.day
        const key: string = `${start}-${end}-${date}-${day}-${slot.parentId}-${slot.type}${slot.placeId ? '-' + slot.placeId : ''}`

        if (!groupedSlotsMap[key]) {
            groupedSlotsMap[key] = { key, slots: [] }
        }
        groupedSlotsMap[key].slots.push(slot)
    })

    const formattedSlots: EventCalendar[] = []

    Object.values(groupedSlotsMap)
        .filter((group: GroupedRegularSlot): boolean => {
            const { slots } = group
            const firstSlot: RegularSlot = slots[0]

            return (
                !datesClosingDays.find((date: Moment): boolean => date.isSame(moment(firstSlot.date, 'DD-MM-YYYY'))) &&
                !(role === Role.parent && firstSlot.absence)
            )
        })
        .map((group: GroupedRegularSlot): void => {
            const { slots } = group
            const firstSlot: RegularSlot = slots[0]

            const start: string = moment(firstSlot.start, 'HH:mm:ss').format('HH:mm')
            const end: string = moment(firstSlot.end, 'HH:mm:ss').format('HH:mm')
            const date: string = moment(firstSlot.date, 'DD-MM-YYYY').format('YYYY-MM-DD')

            if (firstSlot.type === CalendarEventType.REGULAR_REQUEST) {
                if (role === Role.parent) {
                    formattedSlots.push(
                        ...slots
                            .map((request: RegularSlot): EventCalendar[] => {
                                return formatRegularCancellations(request, role)
                            })
                            .flat()
                    )
                } else {
                    formattedSlots.push(...formatRegularCancellationsForRPE(firstSlot, slots))
                }
            } else {
                formattedSlots.push({
                    title: `${start} - ${end} - ${role === Role.parent ? firstSlot.childName : firstSlot.parentName}
                    ${firstSlot.placeName ? ' - ' + firstSlot.placeName : ''}`,
                    start: moment(date + ' ' + start, 'YYYY-MM-DD HH:mm').toDate(),
                    end: moment(date + ' ' + end, 'YYYY-MM-DD HH:mm').toDate(),
                    number: slots.length > 1 ? `${slots.length}` : '',
                    type: firstSlot.type,
                    childId: firstSlot.childId ? firstSlot.childId : undefined,
                    sectionId: firstSlot.sectionId ? firstSlot.sectionId : undefined,
                    slots: formatSlotsRegularSlots(firstSlot, slots),
                    placeId: firstSlot.placeId ? firstSlot.placeId : undefined
                })
            }
        })

    return formattedSlots
}

function formatRegularSlots(regularSlots: RegularSlot[], datesClosingDays: Moment[], role?: Role): EventCalendar[] {
    const groupedSlotsMap: { [key: string]: GroupedRegularSlot } = {}

    regularSlots.forEach((slot: RegularSlot) => {
        const start: string = moment(slot.start, 'HH:mm:ss').format('HH:mm')
        const end: string = moment(slot.end, 'HH:mm:ss').format('HH:mm')
        const date: string = moment(slot.date, 'DD-MM-YYYY').format('DD-MM-YYYY')
        const day: number = slot.day
        const key: string = `${start}-${end}-${date}-${day}-${slot.childId}-${slot.type}${slot.placeId ? '-' + slot.placeId : ''}`

        if (!groupedSlotsMap[key]) {
            groupedSlotsMap[key] = { key, slots: [] }
        }
        groupedSlotsMap[key].slots.push(slot)
    })

    const formattedSlots: EventCalendar[] = []

    Object.values(groupedSlotsMap)
        .filter((group: GroupedRegularSlot): boolean => {
            const { slots } = group
            const firstSlot: RegularSlot = slots[0]

            return (
                !datesClosingDays.find((date: Moment): boolean => date.isSame(moment(firstSlot.date, 'DD-MM-YYYY'))) &&
                !(role === Role.parent && firstSlot.absence)
            )
        })
        .map((group: GroupedRegularSlot): void => {
            const { slots } = group
            const firstSlot: RegularSlot = slots[0]

            const start: string = moment(firstSlot.start, 'HH:mm:ss').format('HH:mm')
            const end: string = moment(firstSlot.end, 'HH:mm:ss').format('HH:mm')
            const date: string = moment(firstSlot.date, 'DD-MM-YYYY').format('YYYY-MM-DD')

            if (firstSlot.type === CalendarEventType.REGULAR_REQUEST) {
                formattedSlots.push(
                    ...slots
                        .map((request: RegularSlot): EventCalendar[] => {
                            return formatRegularCancellations(request, role)
                        })
                        .flat()
                )
            } else {
                formattedSlots.push({
                    title: `${start} - ${end} - ${firstSlot.childName} 
                ${role !== Role.parent && firstSlot.childComplement ? `- ${firstSlot.childComplement} ` : ' '}
                ${firstSlot.placeName ? '- ' + firstSlot.placeName : ''}`,
                    start: moment(date + ' ' + start, 'YYYY-MM-DD HH:mm').toDate(),
                    end: moment(date + ' ' + end, 'YYYY-MM-DD HH:mm').toDate(),
                    number: slots.length > 1 ? `${slots.length}` : '',
                    type: firstSlot.type,
                    childId: firstSlot.childId ? firstSlot.childId : undefined,
                    sectionId: firstSlot.sectionId ? firstSlot.sectionId : undefined,
                    slots: formatSlotsRegularSlots(firstSlot, slots),
                    placeId: firstSlot.placeId ? firstSlot.placeId : undefined
                })
            }
        })

    return formattedSlots
}

function formatSlotsRegularSlots(
    firstSlot: RegularSlot,
    slots: RegularSlot[]
): Absence[] | RegularSlot[] | RegularCancellationRequest[] {
    switch (firstSlot.type) {
        case CalendarEventType.ABSENCE:
            return slots.map((slot: RegularSlot): Absence => formatAbsence(slot))
        case CalendarEventType.REGULAR:
            return slots
        case CalendarEventType.REGULAR_REQUEST:
            return slots.map(
                (slot: RegularSlot): RegularCancellationRequest =>
                    formatRegularCancellation(slot.cancellationRequests[0], firstSlot)
            )
    }

    return []
}

function formatAbsence(regularSlot: RegularSlot): Absence {
    return {
        regularSlotId: regularSlot.id,
        parentComment: regularSlot.parentComment,
        daycareComment: regularSlot.daycareComment,
        date: regularSlot.date,
        start: regularSlot.start,
        end: regularSlot.end,
        childId: regularSlot.childId,
        childName: regularSlot.childName,
        childComplement: regularSlot.childComplement,
        parentId: regularSlot.parentId,
        parentName: regularSlot.parentName
    }
}

function formatRegularCancellationsForRPE(firstSlot: RegularSlot, regularSlots: RegularSlot[]): EventCalendar[] {
    const groupedCancellationsMap: { [key: string]: GroupedRegularCancellation } = {}

    regularSlots
        .map((slot: RegularSlot): RegularCancellationRequest[] => {
            return slot.cancellationRequests.map(
                (request: RegularCancellationRequest): RegularCancellationRequest =>
                    formatRegularCancellation(request, slot)
            )
        })
        .flat()
        .forEach((regularCancellation: RegularCancellationRequest) => {
            const start: string = moment(regularCancellation.start, 'HH:mm:ss').format('HH:mm')
            const end: string = moment(regularCancellation.end, 'HH:mm:ss').format('HH:mm')
            const date: string = moment(regularCancellation.date, 'DD-MM-YYYY').format('DD-MM-YYYY')
            const reason: string = regularCancellation.reason
            const key: string = `${start}-${end}-${date}-${reason}`

            if (!groupedCancellationsMap[key]) {
                groupedCancellationsMap[key] = { key, slots: [] }
            }
            groupedCancellationsMap[key].slots.push(regularCancellation)
        })

    const formattedCancellations: EventCalendar[] = []

    Object.values(groupedCancellationsMap).map((group: GroupedRegularCancellation): void => {
        const { slots } = group
        const firstRegularCancellation: RegularCancellationRequest = slots[0]

        const start: string = moment(firstRegularCancellation.start, 'HH:mm:ss').format('HH:mm')
        const end: string = moment(firstRegularCancellation.end, 'HH:mm:ss').format('HH:mm')
        const date: string = moment(firstSlot.date, 'DD-MM-YYYY').format('YYYY-MM-DD')

        formattedCancellations.push({
            title: `${start} - ${end} - ${firstSlot.parentName}${firstSlot.placeName ? ' - ' + firstSlot.placeName : ''}`,
            start: moment(date + ' ' + start, 'YYYY-MM-DD HH:mm').toDate(),
            end: moment(date + ' ' + end, 'YYYY-MM-DD HH:mm').toDate(),
            number: '',
            type: firstSlot.type,
            childId: firstSlot.childId ? firstSlot.childId : undefined,
            sectionId: firstSlot.sectionId ? firstSlot.sectionId : undefined,
            slots: slots
        })
    })

    return formattedCancellations
}

function formatRegularCancellations(regularSlot: RegularSlot, role?: Role): EventCalendar[] {
    const date: string = moment(regularSlot.date, 'DD-MM-YYYY').format('YYYY-MM-DD')

    return regularSlot.cancellationRequests.map((cancel: RegularCancellationRequest): EventCalendar => {
        const formattedCancellation: RegularCancellationRequest = formatRegularCancellation(cancel, regularSlot)

        const start: string = moment(formattedCancellation.start, 'HH:mm:ss').format('HH:mm')
        const end: string = moment(formattedCancellation.end, 'HH:mm:ss').format('HH:mm')

        return {
            title: `${start} - ${end} - ${regularSlot.childName} 
                ${role !== Role.parent && regularSlot.childComplement ? `- ${regularSlot.childComplement}` : ''}
                ${regularSlot.placeName ? ' - ' + regularSlot.placeName : ''}`,
            start: moment(date + ' ' + start, 'YYYY-MM-DD HH:mm').toDate(),
            end: moment(date + ' ' + end, 'YYYY-MM-DD HH:mm').toDate(),
            number: '',
            type: regularSlot.type,
            childId: regularSlot.childId ? regularSlot.childId : undefined,
            sectionId: regularSlot.sectionId ? regularSlot.sectionId : undefined,
            slots: [formattedCancellation],
            placeId: regularSlot.placeId ? regularSlot.placeId : undefined
        }
    })
}

function formatRegularCancellation(
    cancel: RegularCancellationRequest,
    regularSlot: RegularSlot
): RegularCancellationRequest {
    return {
        id: cancel.id,
        start: cancel.start,
        end: cancel.end,
        reason: cancel.reason,
        date: cancel.date,
        requestDate: cancel.requestDate,
        regularSlotId: regularSlot!.id,
        regularSlotStart: regularSlot!.start,
        regularSlotEnd: regularSlot!.end,
        childId: regularSlot!.childId,
        childName: regularSlot!.childName,
        childComplement: regularSlot!.childComplement,
        parentId: regularSlot!.parentId,
        parentName: regularSlot!.parentName,
        placeId: regularSlot!.placeId,
        placeName: regularSlot!.placeName,
        placeAddress: regularSlot!.placeAddress
    }
}
